<template>
    <div id="return-to-sender-container">
        <card title="Return to Sender">
            <div>
                <b-row>
                    <b-col>
                        <SearchComponent v-model="orderFilter"
                                         :id="onOrderFilterSelected"
                                         label="Enter Order / Tracking Number"
                                         search-u-r-l="/Order/get-order-and-shipped"
                                         :clearable="true"
                                         @input="onOrderFilterSelected">
                        </SearchComponent>
                    </b-col>
                    <b-col>
                        <enum-selector v-model="returnReason"
                                       :enum-codes="returnReasons"
                                       label="Return Reason"
                                       required></enum-selector>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-button variant="info" @click="markAsReturnToSender">
                            Return to Sender
                        </b-button>
                    </b-col>
                </b-row>
            </div>

            <hr/>

            <b-table striped
                     :items="items"
                     :fields="fields"
                     :busy="isLoading"
                     :tbody-tr-class="colorCodeTable"
                     show-empty
                     empty-text="No Return To Sender."
            >
                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading...</strong>
                    </div>
                </template>
                <template #cell(patientName)="data">
                    <router-link :to="{name:'PatientDashboard', params:{id:data.item.patientId}}">
                        {{ data.item.patientName }}
                    </router-link>
                </template>

                <!--    on mouse over / hover logic -->
                <template #cell(returnDate)="row">
                    <b v-b-tooltip.hover :title="row.item.returnReason" style="cursor: pointer">
                        {{ row.value }}
                    </b>
                </template>

                <template #cell(actions)="data">
                    <b-icon icon="x-circle-fill" class="mr-2"
                            v-b-tooltip.hover title="cancel"
                            variant="danger"
                            style="cursor: pointer"
                            @click.prevent="cancelConfirmDialog(data.item.orderId)"></b-icon>
                    <b-icon icon="truck"
                            v-b-tooltip.hover title="ReShip"
                            variant="info"
                            style="cursor: pointer"
                            @click.prevent="setReshipOrder(data.item)"></b-icon>
                </template>
            </b-table>
        </card>

        <!-- Reship Inventory Choice Modal -->
        <b-modal id="reship-inventory-modal"
                 title="Reship Inventory Option"
                 @ok="confirmReship"
                 @cancel="cancelReship"
                 @hidden="cancelReship"
                 ok-title="Reship"
                 cancel-title="Cancel"
                 centered>
            <p>How would you like to handle inventory for this reship?</p>
            <b-form-group>
                <b-form-radio-group v-model="useNewInventory" stacked>
                    <b-form-radio :value="false" class="mb-2">
                        <strong>Use Current Inventory</strong>
                        <div class="text-muted small">Reshipping the returned item - no inventory change</div>
                    </b-form-radio>
                    <b-form-radio :value="true">
                        <strong>Use New Inventory</strong>
                        <div class="text-muted small">Replacing with new item - inventory will be decremented</div>
                    </b-form-radio>
                </b-form-radio-group>
            </b-form-group>
        </b-modal>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import axios, {AxiosError, AxiosRequestConfig} from 'axios';
import {Component, Watch} from 'vue-property-decorator';

import SearchComponent from '@/components/SearchComponent.vue';
import {NotificationOptions} from "@/util/NotificationOptionsPresets";
import moment from "moment";

import {Order, OrderStatus} from "@/models/Order";
import {ReturnReason, ReturnToSender} from "@/models/ReturnToSender";
import EnumSelector from "@/components/EnumSelector.vue";
import {Prescription, RefillStatusCode} from "@/models/Prescription";

@Component({
    name: "ReturnToSenderPage",
    components: {
        EnumSelector,
        SearchComponent
    },
})
export default class ReturnToSenderPage extends Vue {
    private isLoading = false;
    protected items: Array<ReturnToSender> = new Array<ReturnToSender>();
    private orderFilter: Order = new Order();
    private order: Order = new Order();
    private orderStatus = OrderStatus;

    protected returnReasons = ReturnReason;
    private returnReason: ReturnReason = ReturnReason.WrongAddress;

    // Reship inventory choice state
    private reshipOrderItem: ReturnToSender | null = null;
    private useNewInventory: boolean = false;


    private fields: any[] = [
        {key: "orderId", label: 'Order Id', sortable: true, sortDirection: "desc"},
        {key: "patientName", label: 'Patient Name', sortable: true, sortDirection: "desc"},
        {key: "programName", label: "Program Name", sortable: true, sortDirection: "desc"},
        {key: "shipDate", label: "Ship Date", sortable: true, sortDirection: "desc", formatter: this.etaDateFormatter},
        {
            key: "returnDate",
            label: "Return Date",
            sortable: true,
            sortDirection: "desc",
            formatter: this.etaDateFormatter
        },
        {key: "drugName", label: "Drug", sortable: true, sortDirection: "desc"},
        {key: "actions", label: ''},
        {key: "actions", label: ''},
    ];

    created() {
        this.loadItems();
    }

    etaDateFormatter(value: Date, key: string, item: any) {
        if (!value || item.estimatedArrivalDate > item.estimatedArrivalDate) {
            return "";
        }
        return moment.utc(value).format("L");
    }

    loadItems() {
        this.isLoading = true;
        axios.get<Array<ReturnToSender>>('Order/get-all-return-to-sender')
            .then(res => {
                this.items = res.data.map(i => new ReturnToSender(i));
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    onOrderFilterSelected() {
        //console.log(this.orderFilter.id);
        if (this.orderFilter) {
            console.log("order id is selected" + this.orderFilter.id)
        } else {
            console.log('no record selected')
        }
    }

    markAsReturnToSender() {
        if (!this.orderFilter || !this.orderFilter.id) {
            this.$bvModal.msgBoxOk(`No Order selected`);
            return;
        } else {
            const data: ReturnToSender = {
                orderId: this.orderFilter.id as number,
                returnReason: ReturnReason[this.returnReason]
            } as ReturnToSender;
            axios.post(`/Order/return-to-sender`, data)
                .then(response => {
                    this.order = response.data;
                    if (this.order.status == OrderStatus.Canceled) {
                        this.$notification(NotificationOptions.notificationOptionsPreset('Order was mask as return to sender.', NotificationOptions.NotificationTypes.success));
                    }
                })
                .catch(error => {
                    let errorMsg = "Error while loading order";
                    console.error(errorMsg, {error, response: error?.response})
                    this.$notification(NotificationOptions.errorSaveNotificationPreset(errorMsg, error));
                })
                .finally(() => {
                    this.items = []
                    this.loadItems();
                });

        }

        this.loadItems();
    }

    setReshipOrder(order: ReturnToSender) {
        // Show dialog to choose inventory option
        this.reshipOrderItem = order;
        this.useNewInventory = true;  // Default to "Use New Inventory"
        this.$bvModal.show('reship-inventory-modal');
    }

    confirmReship() {
        if (!this.reshipOrderItem) return;

        // Navigate to OrderVerification with inventory choice as query param
        this.$router.push({
            name: 'OrderVerification',
            params: { orderId: this.reshipOrderItem.orderId.toString() },
            query: { useNewInventory: this.useNewInventory ? 'true' : 'false' }
        });

        this.reshipOrderItem = null;
        this.useNewInventory = false;
    }

    cancelReship() {
        this.reshipOrderItem = null;
        this.useNewInventory = false;
    }

    cancelConfirmDialog(orderId: number) {
        this.$bvModal.msgBoxConfirm(`Are you sure you want to cancel this return to sender Order?` + orderId, {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
        })
            .then(value => {
                if (!value) return;
                this.cancelOrder(orderId);
            })
            .catch(err => {
                console.error("Error caught on Cancel button.", err);
            })
            .finally(() => {
                this.items = []
                this.loadItems();
            });


    }

    cancelOrder(orderId: Number) {
        if (!orderId) return;

        axios.post(`/Order/cancel-return-to-order`, orderId)
            .then(response => {
                this.order = response.data;
                if (this.order.status == OrderStatus.Canceled) {
                    // The cancel itself succeeded either way; a warning means a refund did not, and it
                    // already says so, so it replaces the success toast rather than contradicting it.
                    this.$notification(response.data.saveWarning
                        ? NotificationOptions.warning(response.data.saveWarning)
                        : NotificationOptions.notificationOptionsPreset('Order was cancelled.', NotificationOptions.NotificationTypes.success));
                }

            })
            .catch(error => {
                let errorMsg = "Error while loading order";
                console.error(errorMsg, {error, response: error?.response})
                this.$notification(NotificationOptions.errorSaveNotificationPreset(errorMsg, error));
            })
    }

    colorCodeTable(item: ReturnToSender, type: any) {
        if (!item.returnDate) return;
        const cutoff = moment.utc().add(-14, "day");
        const returnDate = moment.utc(item.returnDate);
        if (returnDate.isBefore(cutoff, "day")) return "table-danger";
    }

}

</script>
